import React from 'react';
import PropTypes from 'prop-types';
import { Datagrid, FunctionField, usePermissions, useTranslate, useResourceContext } from 'react-admin';
import { Box, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import SimpleList from '@/components/global/list/SimpleList.jsx';
import { serviceRequestStatusChoices } from '@/components/global/options/selectOptions.jsx';
import ServiceNewIcon from '@/assets/icons/service-new-icon.svg?react';
import ServiceFailedIcon from '@/assets/icons/service-failed-icon.svg?react';
import ServiceProgressIcon from '@/assets/icons/service-progress-icon.svg?react';
import { styled } from '@mui/material/styles';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { Palette } from '@/themes/themeOverrides/Palette';
import NotificationBox from '@/components/global/fields/NotificationBox.jsx';
import { useDrawer } from '@/composables/useDrawer';
import { scrollServiceDrawerToTop } from '@/helpers/ui/scrollServiceDrawerToTop.js';

const StyledServiceHistory = styled('div')(({ shouldExtend }) => ({
    ...(shouldExtend && {
        '& .MuiPaper-root': {
            marginRight: '-20px',
            [`@media (min-width: ${breakpoints.values.md}px)`]: {
                marginRight: '-56px',
            },
        },
        '& .RaDatagrid-tableWrapper': {
            borderRadius: '16px 0 0 0',
        },
    }),
}));

const ServiceHistory = ({ deviceId, shouldExtend = false }) => {
    const resource = useResourceContext();
    const translate = useTranslate();
    const isServiceInspection = resource === 'service-inspection';
    const serviceRequestResource = isServiceInspection ? 'service-inspection' : 'service-request';

    return (
        <StyledServiceHistory shouldExtend={shouldExtend}>
            <SimpleList
                resource={serviceRequestResource}
                filter={{ deviceId }}
                sort={{ field: 'createdAt', order: 'DESC' }}
                empty={
                    <NotificationBox variant='info' className='large'>
                        {translate('resources.serviceRequest.labels.noServiceRequests')}
                    </NotificationBox>
                }
            >
                <Datagrid bulkActionButtons={false}>
                    <FunctionField
                        source='customId'
                        label={translate('resources.serviceRequest.labels.idLabel')}
                        render={record => <IdField record={record} resource={serviceRequestResource} />}
                    />
                    <FunctionField source='status' label={translate('common.status')} render={record => <StatusField record={record} />} />
                    <FunctionField
                        source='createdAt'
                        label={translate('common.createdAt')}
                        sortBy='createdAt'
                        render={record =>
                            record?.createdAt ? (
                                <Tooltip
                                    title={
                                        formatIntervalSince(record.createdAt)
                                            ? `${formatIntervalSince(record.createdAt)} ${translate('notification.timeAgo')}`
                                            : ''
                                    }
                                    arrow
                                    classes={{ tooltip: 'tooltip-black' }}
                                >
                                    <span>{format(new Date(record.createdAt), 'dd.MM.yyyy HH:mm')}</span>
                                </Tooltip>
                            ) : (
                                ''
                            )
                        }
                    />
                    <FunctionField
                        source='closedAt'
                        label={translate('common.closedAt')}
                        sortBy='closedAt'
                        render={record =>
                            record?.closedAt ? (
                                <Tooltip
                                    title={
                                        formatIntervalSince(record.closedAt)
                                            ? `${formatIntervalSince(record.closedAt)} ${translate('notification.timeAgo')}`
                                            : ''
                                    }
                                    arrow
                                    classes={{ tooltip: 'tooltip-black' }}
                                >
                                    <span>{format(new Date(record.closedAt), 'dd.MM.yyyy HH:mm')}</span>
                                </Tooltip>
                            ) : (
                                ''
                            )
                        }
                    />
                    <FunctionField
                        label={translate('resources.serviceRequest.labels.sourceTitle')}
                        sortBy='sourceType'
                        render={record => <SourceField record={record} />}
                    />
                </Datagrid>
            </SimpleList>
        </StyledServiceHistory>
    );
};

const IdField = ({ record, resource }) => {
    const { permissions } = usePermissions();
    const { openDrawer, elementId, type: activeDrawerType } = useDrawer();
    const formattedId = record.customId.toString().padStart(6, '0');

    const handleOpenServiceRequest = event => {
        event.preventDefault();
        event.stopPropagation();
        const drawerType = `${resource}-edit`;
        const isSameDrawerOpen = elementId === record.id && activeDrawerType === drawerType;
        openDrawer(record.id, drawerType);
        if (isSameDrawerOpen) {
            setTimeout(() => scrollServiceDrawerToTop(), 50);
        }
    };

    return permissions.isClient ? (
        <span>#{formattedId}</span>
    ) : (
        <Box
            component='button'
            type='button'
            onClick={handleOpenServiceRequest}
            sx={{
                all: 'unset',
                cursor: 'pointer',
                color: Palette.blue.blue700,
                font: 'inherit',
                '&:hover, &:active, &:focus': {
                    textDecoration: 'underline',
                },
            }}
        >
            #{formattedId}
        </Box>
    );
};

const StatusField = ({ record }) => {
    const translate = useTranslate();

    const status = serviceRequestStatusChoices(translate).find(s => s.id === record.status);
    let icon = <ServiceProgressIcon />;
    if (record.status === 'new') {
        icon = <ServiceNewIcon />;
    } else if (record.status === 'failedContact') {
        icon = <ServiceFailedIcon />;
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '8px' }}>{icon}</span>
            <span>{status.name}</span>
        </div>
    );
};

const SourceField = ({ record }) => {
    const translate = useTranslate();
    let source = '';
    if (record.sourceType === 'auto') {
        source = translate('selectOptions.serviceRequestSourceTypeChoices.auto');
    } else if (record.sourceType === 'collection_point') {
        source = translate('selectOptions.serviceRequestSourceTypeChoices.collectionPoint');
    } else if (record.sourceType === 'user') {
        source = `${record.source.firstName} ${record.source.lastName}`;
    }

    return source;
};

ServiceHistory.propTypes = {
    deviceId: PropTypes.string.isRequired,
};

IdField.propTypes = {
    record: PropTypes.shape({
        id: PropTypes.string.isRequired,
        customId: PropTypes.number.isRequired,
    }),
    resource: PropTypes.string.isRequired,
};

StatusField.propTypes = {
    record: PropTypes.shape({
        id: PropTypes.string.isRequired,
        status: PropTypes.string.isRequired,
    }),
};

SourceField.propTypes = {
    record: PropTypes.shape({
        id: PropTypes.string.isRequired,
        sourceType: PropTypes.string.isRequired,
        source: PropTypes.object,
    }),
};

export default ServiceHistory;
